import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface BulkConfirmationProps {
  /** Products the accepted run creates. */
  totalProducts: number;
  /**
   * Host-framed (Shopify admin): there is no design overview, so the text
   * leaves it out and the primary action returns to the guided entry.
   */
  hostChrome?: boolean;
  onOpenOverview: () => void;
  onBackToStart: () => void;
  className?: string;
}

/**
 * Shown once the server accepted the run (R32): the job continues in the
 * background, the design overview shows its progress.
 */
export function BulkConfirmation({
  totalProducts,
  hostChrome = false,
  onOpenOverview,
  onBackToStart,
  className,
}: BulkConfirmationProps) {
  const { t } = useTranslation();

  return (
    <Card
      variant="section"
      role="status"
      data-testid="bulk-confirmation"
      className={cn(
        'flex flex-col items-center gap-4 px-6 py-12 text-center',
        className
      )}
    >
      <CheckCircle2 className="h-10 w-10 text-success" aria-hidden="true" />
      <h2 className="dc-h1 text-[28px] text-ink">
        {t('seller.bulk.confirmation.title')}
      </h2>
      <p className="max-w-prose text-[15px] text-ink-2">
        {t(
          hostChrome
            ? 'seller.bulk.confirmation.textHost'
            : 'seller.bulk.confirmation.text',
          { count: totalProducts }
        )}
      </p>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        <Button onClick={onOpenOverview}>
          {t(
            hostChrome
              ? 'seller.bulk.confirmation.toCreator'
              : 'seller.bulk.confirmation.toOverview'
          )}
        </Button>
        <Button variant="outline" onClick={onBackToStart}>
          {t('seller.bulk.confirmation.backToStart')}
        </Button>
      </div>
    </Card>
  );
}
